/**
 * KW-584 — Bundle-Split statt Mengen-Aggregation
 * ---------------------------------------------------------------------------
 * Problem: Erhöht der Kunde die Menge einer AVIS-Bundle-Zeile, skaliert der
 * Warenkorb die Mengen der Komponenten hoch (1048 × 15 → 1048 × 30). Für
 * Meterware-Komponenten geht damit die Stückelung verloren: Xentral sieht
 * 30 Meter (ein Zuschnitt, 2 Enden) statt 2 × 15 Meter (2 Zuschnitte, 4 Enden).
 *
 * Lösung: Menge 2 = zwei vollständige Konfigurationsgruppen im Warenkorb.
 * Shopify stapelt Zeilen nur bei identischer Variante UND identischen
 * Properties — eine neue `_apo_order`-ID pro Gruppe verhindert das Stapeln
 * zuverlässig, ohne Shopify-seitige Sonderbehandlung.
 *
 * Der Kunde sieht weiterhin eine Zeile mit Menge N (siehe `collapse`), die
 * Bestellung enthält N getrennte Gruppen.
 *
 * Ausnahme Meterware: bei Produkten, deren Menge Meter statt Stück bedeutet
 * (`1039`, `1040`, `1041`, `1043`, `1166` …), ist die Aggregation korrekt —
 * dort bleibt das alte Verhalten aktiv. Steuerung über `options.semantics`.
 *
 * Reines ES5+/kein Framework, keine DOM-Abhängigkeit — testbar gegen jede
 * Implementierung der Shopify Cart AJAX API.
 */
(function (global) {
  'use strict';

  /** Properties, die zwei ansonsten identische Konfigurationen unterscheiden
   *  dürfen, ohne dass sie als verschiedene Produkte gelten. */
  var SIGNATURE_IGNORE = ['_apo_order', '_kw_unit', '_kw_unit_key'];

  var ADDED_BY = /^Added by:\s*(\S+)/;
  var MULTIPLIER = /\*\s*(\d+)\s*$/;

  // ── Line-Item-Klassifikation ──────────────────────────────────────────────

  function props(item) {
    return item && item.properties ? item.properties : {};
  }

  /** ID der Gruppe, wenn `item` der Bundle-Parent ist — sonst null. */
  function parentGroupId(item) {
    var v = props(item)._apo_order;
    if (typeof v !== 'string' || !v) return null;
    if (ADDED_BY.test(v)) return null;
    return v.trim();
  }

  /** ID der Gruppe, zu der `item` als AVIS-Komponente gehört — sonst null. */
  function childGroupId(item) {
    var v = props(item)._apo_order;
    if (typeof v !== 'string') return null;
    var m = v.match(ADDED_BY);
    return m ? m[1] : null;
  }

  /** Menge pro Parent-Einheit ("Added by: 123 * 15" → 15). */
  function childMultiplier(item) {
    var v = props(item)._apo_order || '';
    var m = String(v).match(MULTIPLIER);
    return m ? parseInt(m[1], 10) : null;
  }

  /**
   * Konfigurations-Signatur des Parents. Der AVIS-Parent trägt die komplette
   * Auswahl als sichtbare Properties, deshalb reicht Variante + Properties.
   */
  function signature(item) {
    var p = props(item);
    var keys = [];
    for (var k in p) {
      if (Object.prototype.hasOwnProperty.call(p, k) && SIGNATURE_IGNORE.indexOf(k) === -1) {
        keys.push(k);
      }
    }
    keys.sort();
    var parts = [String(item.variant_id)];
    for (var i = 0; i < keys.length; i++) parts.push(keys[i] + '=' + p[keys[i]]);
    return parts.join('');
  }

  // ── Gruppen & Einheiten ───────────────────────────────────────────────────

  /**
   * Zerlegt einen Warenkorb in Bundle-Gruppen (Parent + Komponenten) und
   * Einzelzeilen ohne AVIS-Bezug.
   *
   * @param {{items: Array}} cart  Antwort von /cart.js
   * @returns {{groups: Array, loose: Array}}
   */
  function buildGroups(cart) {
    var items = (cart && cart.items) || [];
    var groups = [];
    var byId = {};
    var loose = [];
    var i;

    for (i = 0; i < items.length; i++) {
      var gid = parentGroupId(items[i]);
      if (gid) {
        var group = {
          id: gid,
          parent: items[i],
          children: [],
          signature: signature(items[i]),
          firstIndex: i,
          total: items[i].line_price || 0
        };
        groups.push(group);
        byId[gid] = group;
      }
    }

    for (i = 0; i < items.length; i++) {
      if (parentGroupId(items[i])) continue;
      var cid = childGroupId(items[i]);
      if (cid && byId[cid]) {
        byId[cid].children.push(items[i]);
        byId[cid].total += items[i].line_price || 0;
      } else {
        loose.push(items[i]);
      }
    }

    return { groups: groups, loose: loose };
  }

  /**
   * Schlüssel der Anzeige-Einheit. Klone tragen `_kw_unit_key` mit der ID der
   * Basisgruppe — daran erkennen Theme und Warenkorb zusammengehörige Gruppen,
   * ohne Properties vergleichen zu müssen. Ohne Klon-Marker fällt die Zuordnung
   * auf die Konfigurations-Signatur zurück.
   */
  function unitKey(parentItem) {
    var k = props(parentItem)._kw_unit_key;
    if (typeof k === 'string' && k) return k.trim();
    return parentGroupId(parentItem) || signature(parentItem);
  }

  /**
   * Fasst zusammengehörige Gruppen zu einer Anzeige-Einheit zusammen — das ist
   * die Kundensicht: eine Zeile, Menge = Anzahl der Gruppen.
   */
  function collapse(groups) {
    var units = [];
    var bySig = {};
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      g.unitKey = unitKey(g.parent);
      var unit = bySig[g.unitKey];
      if (!unit) {
        unit = {
          signature: g.unitKey,
          groups: [],
          parent: g.parent,
          firstIndex: g.firstIndex,
          quantity: 0,
          total: 0
        };
        bySig[g.unitKey] = unit;
        units.push(unit);
      }
      unit.groups.push(g);
      unit.quantity += 1;
      unit.total += g.total;
    }

    // `/cart/add.js` stellt neue Zeilen VORNE ein — die Reihenfolge im
    // Warenkorb sagt also nichts über Basis und Klon. Basisgruppe (ohne
    // `_kw_unit_key`) nach vorne holen, damit beim Verringern der Menge immer
    // die Klone verschwinden und die sichtbare Zeile stehen bleibt.
    for (var u = 0; u < units.length; u++) {
      units[u].groups.sort(function (a, b) {
        return (props(a.parent)._kw_unit_key ? 1 : 0) - (props(b.parent)._kw_unit_key ? 1 : 0);
      });
      units[u].parent = units[u].groups[0].parent;
      units[u].firstIndex = units[u].groups[0].firstIndex;
    }

    return units;
  }

  /**
   * Vollständige Analyse eines Warenkorbs.
   * `semantics(parentItem)` liefert 'stueck' (Default) oder 'meter'.
   */
  function analyze(cart, semantics) {
    var result = buildGroups(cart);
    var units = collapse(result.groups);
    for (var i = 0; i < units.length; i++) {
      units[i].semantics = (semantics && semantics(units[i].parent)) || 'stueck';
      units[i].splittable = units[i].semantics !== 'meter';
      if (!units[i].splittable) {
        // Meterware: Menge steht am Parent, nicht in der Gruppenzahl.
        units[i].quantity = units[i].groups[0].parent.quantity;
      }
    }
    return { groups: result.groups, loose: result.loose, units: units };
  }

  // ── Payloads für die Cart AJAX API ────────────────────────────────────────

  function nextGroupId(cart) {
    var used = {};
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var gid = parentGroupId(items[i]) || childGroupId(items[i]);
      if (gid) used[gid] = true;
    }
    var id = Date.now();
    while (used[String(id)]) id += 1;
    return String(id);
  }

  function cloneProps(source, overrides) {
    var out = {};
    for (var k in source) {
      if (Object.prototype.hasOwnProperty.call(source, k)) out[k] = source[k];
    }
    for (var o in overrides) {
      if (Object.prototype.hasOwnProperty.call(overrides, o)) out[o] = overrides[o];
    }
    return out;
  }

  /**
   * Baut den /cart/add.js-Body, der eine Gruppe 1:1 dupliziert.
   * Alle Properties bleiben erhalten, nur die Gruppen-ID ist neu — dadurch
   * legt Shopify die Zeilen nicht mit dem Original zusammen.
   *
   * @param {Object} group      Gruppe aus buildGroups()
   * @param {String} newId      neue Gruppen-ID
   * @param {Number} unitNumber laufende Nummer der Einheit (für _kw_unit)
   * @param {String} key        Schlüssel der Anzeige-Einheit (ID der Basisgruppe)
   */
  function clonePayload(group, newId, unitNumber, key) {
    var mark = {
      _kw_unit: String(unitNumber),
      _kw_unit_key: key || unitKey(group.parent)
    };

    var items = [{
      id: group.parent.variant_id,
      quantity: group.parent.quantity,
      properties: cloneProps(props(group.parent), cloneProps(mark, { _apo_order: newId }))
    }];

    for (var i = 0; i < group.children.length; i++) {
      var child = group.children[i];
      var mult = childMultiplier(child);
      items.push({
        id: child.variant_id,
        quantity: child.quantity,
        // Nested Cart Line: Komponente haengt unter dem Parent (Checkout zeigt sie eingerueckt).
        parent_id: group.parent.variant_id,
        properties: cloneProps(props(child), cloneProps(mark, {
          _apo_order: 'Added by: ' + newId + (mult === null ? '' : ' * ' + mult)
        }))
      });
    }

    return { items: items };
  }

  /** Baut den /cart/update.js-Body, der eine komplette Gruppe entfernt. */
  function removePayload(group) {
    var updates = {};
    updates[group.parent.key] = 0;
    for (var i = 0; i < group.children.length; i++) updates[group.children[i].key] = 0;
    return { updates: updates };
  }

  /** Alte Mengen-Skalierung — weiterhin korrekt für Meterware. */
  function scalePayload(cart, group, newQty) {
    var oldQty = group.parent.quantity || 1;
    var updates = {};
    updates[group.parent.key] = newQty;
    for (var i = 0; i < group.children.length; i++) {
      var child = group.children[i];
      var perParent = child.quantity / oldQty;
      updates[child.key] = newQty <= 0 ? 0 : Math.round(perParent * newQty);
    }
    return { updates: updates };
  }

  // ── Ausführung ────────────────────────────────────────────────────────────

  /**
   * Setzt die vom Kunden gewünschte Menge einer Einheit um.
   *
   * @param {Object} api  { get(), add(body), update(body) } → Promises auf cart
   * @param {String} sig  Signatur der Einheit
   * @param {Number} want Zielmenge
   * @param {Function} semantics
   * @returns {Promise<Object>} neuer Warenkorb
   */
  function setQuantity(api, sig, want, semantics) {
    return api.get().then(function (cart) {
      var state = analyze(cart, semantics);
      var unit = null;
      for (var i = 0; i < state.units.length; i++) {
        if (state.units[i].signature === sig) { unit = state.units[i]; break; }
      }
      if (!unit) return cart;

      // Meterware — oder Altbestand: eine bereits aggregierte Zeile (Parent mit
      // Menge > 1) wird nicht nachträglich zerlegt, sonst vervielfacht sich die
      // Menge. Solche Warenkörbe behalten das bisherige Verhalten.
      if (!unit.splittable || unit.groups[0].parent.quantity !== 1) {
        if (want <= 0) return api.update(removePayload(unit.groups[0]));
        return api.update(scalePayload(cart, unit.groups[0], want));
      }

      var have = unit.groups.length;
      if (want === have) return cart;

      if (want < have) {
        // Jüngste Gruppen zuerst entfernen — die zuletzt hinzugefügte geht.
        var doomed = unit.groups.slice(want);
        var updates = {};
        for (var d = 0; d < doomed.length; d++) {
          var body = removePayload(doomed[d]);
          for (var k in body.updates) {
            if (Object.prototype.hasOwnProperty.call(body.updates, k)) updates[k] = 0;
          }
        }
        return api.update({ updates: updates });
      }

      // Aufstocken: Gruppen einzeln klonen, damit jede eine eigene ID bekommt.
      var template = unit.groups[unit.groups.length - 1];
      var chain = Promise.resolve(cart);
      var toAdd = want - have;
      for (var n = 0; n < toAdd; n++) {
        chain = chain.then(function (current) {
          var fresh = analyze(current, semantics);
          var live = null;
          for (var u = 0; u < fresh.units.length; u++) {
            if (fresh.units[u].signature === sig) { live = fresh.units[u]; break; }
          }
          var src = live ? live.groups[live.groups.length - 1] : template;
          var base = live ? live.groups[0] : template;
          var count = live ? live.groups.length : have;
          return api.add(clonePayload(src, nextGroupId(current), count + 1, unitKey(base.parent)));
        });
      }
      return chain;
    });
  }

  // ── Nested Cart Lines ─────────────────────────────────────────────────────
  //
  // Shopify verschachtelt Warenkorbzeilen (Cart AJAX API `parent_id` /
  // `parent_line_key`). Checkout, Bestellbestaetigung und Bestellstatus zeigen
  // die Komponenten dann eingerueckt unter dem Parent statt als lose Zeilen.
  // Der Avis-Konfigurator legt Gruppen ohne Verschachtelung an; `normalize`
  // haengt sie einmalig um (Gruppe entfernen, verschachtelt neu anlegen).
  // Properties, Mengen und Preise bleiben identisch, nur die Zeilen-Keys
  // aendern sich.

  /** true, wenn alle Komponenten der Gruppe unter ihrem Parent haengen. */
  function isNested(group) {
    if (!group.children.length) return true;
    for (var i = 0; i < group.children.length; i++) {
      var rel = group.children[i].parent_relationship;
      if (!rel || rel.parent_key !== group.parent.key) return false;
    }
    return true;
  }

  /** /cart/add.js-Body, der die Gruppe 1:1 verschachtelt neu anlegt. */
  function nestPayload(group) {
    var items = [{
      id: group.parent.variant_id,
      quantity: group.parent.quantity,
      properties: cloneProps(props(group.parent), {})
    }];
    for (var i = 0; i < group.children.length; i++) {
      var child = group.children[i];
      items.push({
        id: child.variant_id,
        quantity: child.quantity,
        parent_id: group.parent.variant_id,
        properties: cloneProps(props(child), {})
      });
    }
    return { items: items };
  }

  // ── Aris-Modus "Merge with main product" ──────────────────────────────────
  //
  // Live laeuft Aris im Modus "Display separately": Komponenten sind eigene
  // Zeilen ("Added by: <id> * n"). Steht die App auf "Merge with main product"
  // (z. B. Staging), haengt die Komponentenliste nur als `_apo_bundles`
  // ("<variant>*<qty>*<price>,...") in der Property `_apo_options` am Parent.
  // Solche Parents werden hier in die Live-Struktur ueberfuehrt, damit
  // Warenkorb, Split und Checkout identisch laufen. Mit "Display separately"
  // (live) gibt es kein `_apo_options`, der Zweig greift dort nie.

  function mergedBundleSpec(item) {
    var raw = props(item)._apo_options;
    if (typeof raw !== 'string' || !raw) return null;
    if (parentGroupId(item) || childGroupId(item)) return null;
    var list;
    try { list = JSON.parse(raw); } catch (e) { return null; }
    if (!Array.isArray(list)) return null;

    var bundles = null;
    var options = [];
    for (var i = 0; i < list.length; i++) {
      var o = list[i] || {};
      if (o.name === '_apo_bundles') bundles = String(o.value || '');
      else if (typeof o.name === 'string' && o.name.charAt(0) !== '_') options.push(o);
    }
    if (!bundles) return null;

    var parts = bundles.split(',');
    var components = [];
    for (var n = 0; n < parts.length; n++) {
      var seg = parts[n].split('*');
      var vid = parseInt(seg[0], 10);
      var qty = parseInt(seg[1], 10);
      if (!vid || !qty) continue;
      components.push({
        variant_id: vid,
        quantity: qty,
        option: options.length === parts.length ? options[n] : null
      });
    }
    return components.length ? { components: components } : null;
  }

  /**
   * /cart/add.js-Body: Parent (mit _apo_order) + Komponenten wie live, verschachtelt.
   * `_apo_options` / `_apo_addons` werden am Parent entfernt: Mit diesen Properties
   * macht die Aris-Cart-Transform aus dem Parent ein Shopify-Bundle (item_components),
   * an das sich keine Nested Lines haengen lassen ("Bundle products and add-ons
   * cannot be mixed"). Live tragen Parents diese Properties nicht.
   */
  function splitPayload(item, spec, newId) {
    var parentProps = cloneProps(props(item), { _apo_order: newId, _has_apo: 'true' });
    delete parentProps._apo_options;
    delete parentProps._apo_addons;
    delete parentProps._apo_bundles;
    var items = [{
      id: item.variant_id,
      quantity: item.quantity,
      properties: parentProps
    }];
    for (var i = 0; i < spec.components.length; i++) {
      var c = spec.components[i];
      var cp = { _apo_order: 'Added by: ' + newId + ' * ' + c.quantity };
      if (c.option) cp._apo_option = c.option.name + ': ' + c.option.value;
      items.push({
        id: c.variant_id,
        quantity: c.quantity * (item.quantity || 1),
        parent_id: item.variant_id,
        properties: cp
      });
    }
    return { items: items };
  }

  /**
   * Haengt alle nicht verschachtelten Gruppen um und ueberfuehrt Aris-
   * "Merge"-Parents in die Live-Struktur. Loest mit { cart, changed } auf;
   * `changed` = Anzahl geaenderter Gruppen.
   */
  function normalize(api) {
    return api.get().then(function (cart) {
      var merged = [];
      for (var m = 0; m < cart.items.length; m++) {
        var spec = mergedBundleSpec(cart.items[m]);
        if (spec) merged.push({ item: cart.items[m], spec: spec });
      }
      if (!merged.length) return cart;

      var chain = Promise.resolve(cart);
      var base = Date.now();
      merged.forEach(function (entry, idx) {
        chain = chain
          .then(function () { var u = {}; u[entry.item.key] = 0; return api.update({ updates: u }); })
          .then(function () { return api.add(splitPayload(entry.item, entry.spec, String(base + idx))); });
      });
      return chain.then(function (latest) { latest.__kwSplitCount = merged.length; return latest; });
    }).then(function (cart) {
      var already = cart.__kwSplitCount || 0;
      var groups = buildGroups(cart).groups;
      var todo = [];
      for (var i = 0; i < groups.length; i++) {
        if (!isNested(groups[i])) todo.push(groups[i]);
      }
      if (!todo.length) return { cart: cart, changed: already };

      var chain = Promise.resolve(cart);
      todo.forEach(function (group) {
        chain = chain
          .then(function () { return api.update(removePayload(group)); })
          .then(function () { return api.add(nestPayload(group)); });
      });
      return chain.then(function (latest) {
        return { cart: latest, changed: already + todo.length };
      });
    });
  }

  global.CdxBundleSplit = {
    mergedBundleSpec: mergedBundleSpec,
    splitPayload: splitPayload,
    isNested: isNested,
    nestPayload: nestPayload,
    normalize: normalize,
    SIGNATURE_IGNORE: SIGNATURE_IGNORE,
    parentGroupId: parentGroupId,
    childGroupId: childGroupId,
    childMultiplier: childMultiplier,
    signature: signature,
    unitKey: unitKey,
    buildGroups: buildGroups,
    collapse: collapse,
    analyze: analyze,
    nextGroupId: nextGroupId,
    clonePayload: clonePayload,
    removePayload: removePayload,
    scalePayload: scalePayload,
    setQuantity: setQuantity
  };
})(typeof window !== 'undefined' ? window : this);
